import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { isEmpty } from 'lodash-es';
import { bindToContent, CmsEntities } from 'client/data/models/cms';
import { connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { Spinner } from 'site-modules/shared/components/spinner/spinner';
import { DEFAULT_CONTENT } from 'client/data/cms/content';
import { useScrollIntoViewTracking } from 'site-modules/shared/hooks/use-scroll-into-view-tracking';
import { TrackingConstant } from 'client/tracking/constant';

import { getCustomerReviewSummaryData } from './user-reviews-utils';
import { REVIEWS_MODULE_CREATIVE_ID, REVIEWS_MODULE_USER_REVIEW_CONTENT_PATH } from './constants';
import { ReviewsSummary } from './reviews-summary/reviews-summary';
import { ReviewsList } from './reviews-list/reviews-list';

import './offers-reviews-module.scss';

const WIDGET_VIEW_EVENT_DATA = {
  event_type: TrackingConstant.EVENT_TYPE_WIDGET_VIEW,
  event_data: {
    action_cause: TrackingConstant.PAGE_LOAD_CAUSE,
    action_name: TrackingConstant.ACTION_WIDGET_VIEW,
    creative_id: REVIEWS_MODULE_CREATIVE_ID,
  },
};

export const OffersReviewsModuleUI = ({ customerReviewContent = DEFAULT_CONTENT, isWithSpinner = false }) => {
  const ref = useRef(null);
  const isLoading = customerReviewContent?.isDefault;
  const hasContent = !isEmpty(customerReviewContent);

  useScrollIntoViewTracking({
    eventData: WIDGET_VIEW_EVENT_DATA,
    additionalConditions: !isLoading && hasContent,
    ref,
  });

  if ((isLoading && !isWithSpinner) || !hasContent) return null;

  const summaryData = getCustomerReviewSummaryData(customerReviewContent);

  const userReviews = [];
  const userReviewsContent = customerReviewContent.children().filter(({ id }) => /reviews-\d{4}/.test(id));
  userReviewsContent.forEach(yearOfReviews => {
    const yearEntries = yearOfReviews.children();
    yearEntries.forEach(entry => {
      const { customerRating: rating, customerReview: text, reviewDate: date } = entry.getAllMetadata();
      if (text) {
        userReviews.push({ rating: parseFloat(rating), text, date });
      }
    });
  });

  return (
    <ScrollElement id="reviews-module">
      <div className="reviews-module" data-tracking-parent={REVIEWS_MODULE_CREATIVE_ID} ref={ref} data-no-widget-view>
        {isLoading && isWithSpinner ? (
          <div className="d-flex justify-content-center">
            <Spinner size={40} thickness={3} color="gray" />
          </div>
        ) : (
          <>
            <h2 className="size-24 fw-bold text-cool-gray-10 mb-0_5">What customers are saying</h2>
            <p className="size-16 text-cool-gray-30 mb-1_5">
              Join thousands of happy customers who redeemed an offer using Edmunds.
            </p>
            <hr className="border-0 my-1_5" />
            <Row>
              <Col xs={12} lg={5} className="pe-lg-1_25">
                <ReviewsSummary {...summaryData} />
              </Col>
              <Col xs={12} lg={7} className="ps-lg-1_25">
                <>
                  <hr className="border-0 my-1_5 d-lg-none" />
                  <ReviewsList totalReviewsNumber={summaryData.totalReviewsNumber} allReviews={userReviews} />
                </>
              </Col>
            </Row>
          </>
        )}
      </div>
    </ScrollElement>
  );
};

OffersReviewsModuleUI.propTypes = {
  customerReviewContent: CmsEntities.Content,
  isWithSpinner: PropTypes.bool,
};

export const stateToPropsConfig = {
  customerReviewContent: bindToContent(() => REVIEWS_MODULE_USER_REVIEW_CONTENT_PATH),
};

export const OffersReviewsModule = connectToModel(OffersReviewsModuleUI, stateToPropsConfig);
